import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, Filter, UserCheck, UserX, Edit, Mail, Shield, Key, Trash2 } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminUsers = () => {
  const { user: currentUser } = useAuth();
  const isPlatformOwner = currentUser?.platform_role === 'platform_owner';
  const isPlatformStaff = currentUser?.platform_role === 'platform_staff';
  const canResetPassword = isPlatformOwner || isPlatformStaff;
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPlatformOwnerConfirm, setShowPlatformOwnerConfirm] = useState(false);
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    role: 'all',
    platform_role: 'all',
    status: 'all'
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.role !== 'all') params.append('role', filters.role);
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);
      params.append('limit', '1000');
      
      const response = await axios.get(`${API}/admin/users?${params}`);
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser({
      ...user,
      is_active: user.status === 'active'
    });
    setShowEditModal(true);
  };

  const handleSave = async () => {
    // Check if assigning platform_owner
    if (editingUser.platform_role === 'platform_owner' && !showPlatformOwnerConfirm) {
      setShowPlatformOwnerConfirm(true);
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.patch(`${API}/admin/users/${editingUser.id}`, {
        full_name: editingUser.full_name,
        email: editingUser.email,
        role: editingUser.role,
        platform_role: editingUser.platform_role || 'none',
        is_active: editingUser.is_active,
        onboarding_intent: editingUser.onboarding_intent
      });
      
      toast.success('User updated successfully');
      setShowEditModal(false);
      setShowPlatformOwnerConfirm(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to update user';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (userToDelete) => {
    // Check if trying to delete primary owner
    if (userToDelete.email === 'vincotek25@gmail.com') {
      toast.error('Cannot delete primary owner account');
      return;
    }
    
    const reason = prompt(`Please provide a reason for deleting ${userToDelete.full_name}:`);
    if (!reason) return;
    
    const confirm = window.confirm(
      `Are you sure you want to delete ${userToDelete.full_name} (${userToDelete.email})? This action cannot be undone.`
    );
    if (!confirm) return;
    
    try {
      await axios.delete(`${API}/admin/users/${userToDelete.id}?reason=${encodeURIComponent(reason)}`);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handlePasswordReset = (user) => {
    setResetPasswordUser(user);
    setNewPassword('');
    setGeneratedPassword('');
    setShowPasswordResetModal(true);
  };

  const resetPassword = async (method) => {
    if (method === 'set' && newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    try {
      const response = await axios.post(`${API}/admin/reset-user-password`, {
        user_id: resetPasswordUser.id,
        new_password: method === 'set' ? newPassword : null,
        method: method
      });

      setGeneratedPassword(response.data.temporary_password);
      toast.success('Password reset successfully');
      
      if (method === 'generated') {
        // Keep modal open to show generated password
      } else {
        setTimeout(() => {
          setShowPasswordResetModal(false);
          setResetPasswordUser(null);
        }, 3000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleSuspend = async (userId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'suspended' : 'active';
    const reason = prompt(`Reason for ${newStatus === 'suspended' ? 'suspending' : 'activating'} this user:`);
    
    if (!reason) return;

    try {
      await axios.patch(`${API}/admin/users/${userId}/status`, { status: newStatus, reason });
      toast.success(`User ${newStatus}`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      tradie: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      customer: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
    };
    return <Badge className={colors[role] || ''}>{role}</Badge>;
  };

  const getPlatformRoleBadge = (platformRole) => {
    if (!platformRole || platformRole === 'none') return null;
    const colors = {
      platform_owner: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      platform_staff: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
    };
    const labels = {
      platform_owner: 'Owner',
      platform_staff: 'Staff'
    };
    return (
      <Badge className={colors[platformRole] || ''}>
        <Shield className="w-3 h-3 mr-1" />
        {labels[platformRole]}
      </Badge>
    );
  };

  const getStatusBadge = (status) => {
    return status === 'active' 
      ? <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Active</Badge>
      : <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">Suspended</Badge>;
  };

  const filteredUsers = users.filter(user => {
    if (filters.role !== 'all' && user.role !== filters.role) return false;
    if (filters.platform_role !== 'all' && user.platform_role !== filters.platform_role) return false;
    if (filters.status !== 'all' && user.status !== filters.status) return false;
    if (filters.search && !user.email.toLowerCase().includes(filters.search.toLowerCase()) && 
        !user.full_name.toLowerCase().includes(filters.search.toLowerCase())) return false;
    return true;
  });

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">User Management</h1>
          <p className="text-muted-foreground">Manage all platform users</p>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="md:col-span-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by email or name..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    className="pl-10"
                  />
                </div>
              </div>
              <select
                value={filters.role}
                onChange={(e) => setFilters({ ...filters, role: e.target.value })}
                className="px-3 py-2 border border-border rounded-lg bg-background text-foreground"
              >
                <option value="all">All Roles</option>
                <option value="customer">Customer</option>
                <option value="tradie">Tradie</option>
                <option value="admin">Admin</option>
              </select>
              <select
                value={filters.platform_role}
                onChange={(e) => setFilters({ ...filters, platform_role: e.target.value })}
                className="px-3 py-2 border border-border rounded-lg bg-background text-foreground"
              >
                <option value="all">All Platform Roles</option>
                <option value="none">None</option>
                <option value="platform_staff">Platform Staff</option>
                <option value="platform_owner">Platform Owner</option>
              </select>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="px-3 py-2 border border-border rounded-lg bg-background text-foreground"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
            <div className="mt-3">
              <button
                onClick={fetchUsers}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
              >
                Apply Filters
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground mb-1">Total Users</div>
              <div className="text-2xl font-bold text-foreground">{users.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground mb-1">Customers</div>
              <div className="text-2xl font-bold text-blue-600">{users.filter(u => u.role === 'customer').length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground mb-1">Tradies</div>
              <div className="text-2xl font-bold text-green-600">{users.filter(u => u.role === 'tradie').length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground mb-1">Admins</div>
              <div className="text-2xl font-bold text-purple-600">{users.filter(u => u.role === 'admin').length}</div>
            </CardContent>
          </Card>
        </div>

        {/* Users Table */}
        <Card>
          <CardHeader>
            <CardTitle>All Users ({filteredUsers.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-foreground">User</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Role</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Platform</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Joined</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="px-4 py-8 text-center text-muted-foreground">
                        Loading users...
                      </td>
                    </tr>
                  ) : filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-4 py-8 text-center text-muted-foreground">
                        No users found
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((user) => (
                      <tr key={user.id} className="border-b hover:bg-muted/30">
                        <td className="px-4 py-3">
                          <div>
                            <div className="font-medium text-foreground">{user.full_name}</div>
                            <div className="text-sm text-muted-foreground flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {user.email}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {getRoleBadge(user.role)}
                        </td>
                        <td className="px-4 py-3">
                          {getPlatformRoleBadge(user.platform_role)}
                        </td>
                        <td className="px-4 py-3">
                          {getStatusBadge(user.status)}
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEdit(user)}
                              className="p-2 hover:bg-muted rounded-lg"
                              title="Edit user"
                            >
                              <Edit className="w-4 h-4 text-blue-600" />
                            </button>
                            {canResetPassword && (
                              <button
                                onClick={() => handlePasswordReset(user)}
                                className="p-2 hover:bg-muted rounded-lg"
                                title="Reset password"
                              >
                                <Key className="w-4 h-4 text-purple-600" />
                              </button>
                            )}
                            {isPlatformOwner && user.email !== 'vincotek25@gmail.com' && (
                              <button
                                onClick={() => handleDeleteUser(user)}
                                className="p-2 hover:bg-muted rounded-lg"
                                title="Delete user"
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </button>
                            )}
                            {user.status === 'active' ? (
                              <button
                                onClick={() => handleSuspend(user.id, user.status)}
                                className="p-2 hover:bg-muted rounded-lg"
                                title="Suspend user"
                              >
                                <UserX className="w-4 h-4 text-red-600" />
                              </button>
                            ) : (
                              <button
                                onClick={() => handleSuspend(user.id, user.status)}
                                className="p-2 hover:bg-muted rounded-lg"
                                title="Activate user"
                              >
                                <UserCheck className="w-4 h-4 text-green-600" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
        
        {/* Edit Modal */}
        {showEditModal && editingUser && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-card rounded-lg p-6 max-w-md w-full mx-4 border border-border max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-bold mb-4 text-foreground">Edit User</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium block mb-2 text-foreground">Full Name</label>
                  <Input
                    value={editingUser.full_name}
                    onChange={(e) => setEditingUser({ ...editingUser, full_name: e.target.value })}
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium block mb-2 text-foreground">Email</label>
                  <Input
                    type="email"
                    value={editingUser.email}
                    onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground mt-1">Changing email may affect user login</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium block mb-2 text-foreground">Role</label>
                  <select
                    value={editingUser.role}
                    onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                  >
                    <option value="customer">Customer</option>
                    <option value="tradie">Tradie</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                
                <div>
                  <label className="text-sm font-medium block mb-2 text-foreground">Platform Role (Read-Only)</label>
                  <div className="w-full px-3 py-2 border border-border rounded-lg bg-muted text-foreground">
                    {editingUser.platform_role === 'platform_owner' ? 'Platform Owner' :
                     editingUser.platform_role === 'platform_staff' ? 'Platform Staff' : 
                     'None'}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Platform roles can only be managed in <strong>/admin/manage-admins</strong>
                  </p>
                </div>
                
                <div>
                  <label className="text-sm font-medium block mb-2 text-foreground">Active Status</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editingUser.is_active}
                      onChange={(e) => setEditingUser({ ...editingUser, is_active: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-foreground">
                      {editingUser.is_active ? 'Active' : 'Suspended'}
                    </span>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium block mb-2 text-foreground">Onboarding Intent</label>
                  <select
                    value={editingUser.onboarding_intent || 'PERSONAL'}
                    onChange={(e) => setEditingUser({ ...editingUser, onboarding_intent: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                  >
                    <option value="PERSONAL">Personal</option>
                    <option value="BUSINESS">Business</option>
                  </select>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      setShowPlatformOwnerConfirm(false);
                      setEditingUser(null);
                    }}
                    className="flex-1 px-4 py-2 border border-border rounded-lg hover:bg-muted text-foreground"
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Platform Owner Confirmation */}
        {showPlatformOwnerConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
            <div className="bg-card rounded-lg p-6 max-w-sm w-full mx-4 border border-border">
              <h3 className="text-lg font-bold mb-2 text-foreground">Confirm Platform Owner</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Are you sure you want to grant Platform Owner role to this user? 
                This gives them full administrative access to the platform console.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowPlatformOwnerConfirm(false)}
                  className="flex-1 px-4 py-2 border border-border rounded-lg hover:bg-muted text-foreground"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Password Reset Modal */}
        {showPasswordResetModal && resetPasswordUser && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-card rounded-lg p-6 max-w-md w-full mx-4 border border-border">
              <h3 className="text-xl font-bold mb-4 text-foreground">Reset Password</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Resetting password for: <strong>{resetPasswordUser.full_name}</strong> ({resetPasswordUser.email})
              </p>
              
              {generatedPassword ? (
                <div className="space-y-4">
                  <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <p className="text-sm font-semibold text-yellow-800 dark:text-yellow-200 mb-2">
                      Temporary Password Generated:
                    </p>
                    <p className="text-lg font-mono text-foreground bg-white dark:bg-gray-800 p-2 rounded border">
                      {generatedPassword}
                    </p>
                    <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-2">
                      ⚠️ Copy this password now. It will not be shown again. User must change it on first login.
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setShowPasswordResetModal(false);
                      setResetPasswordUser(null);
                      setGeneratedPassword('');
                    }}
                    className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium block mb-2 text-foreground">
                      Option 1: Set Custom Password
                    </label>
                    <Input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Min 8 characters"
                      autoComplete="new-password"
                    />
                  </div>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={() => resetPassword('set')}
                      disabled={newPassword.length < 8}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      Set Password
                    </button>
                    <button
                      onClick={() => resetPassword('generated')}
                      className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                    >
                      Generate Random
                    </button>
                  </div>
                  
                  <button
                    onClick={() => {
                      setShowPasswordResetModal(false);
                      setResetPasswordUser(null);
                      setNewPassword('');
                    }}
                    className="w-full px-4 py-2 border border-border rounded-lg hover:bg-muted text-foreground"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminUsers;
